import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Camera, Upload, User, Loader2, X, Check } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const ProfilePictureUpload = ({ 
  currentImage, 
  userId, 
  userType = 'user', // 'user' or 'driver'
  backendUrl,
  onUploadSuccess,
  size = 'md' // 'sm', 'md', 'lg'
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Construct full URL for the image
  const getFullImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    // If it's already a full URL or data URL, return as-is
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://') || imageUrl.startsWith('data:')) {
      return imageUrl;
    }
    // If it's a relative URL starting with /api/, prepend backend URL
    if (imageUrl.startsWith('/api/')) {
      return `${backendUrl}${imageUrl}`;
    }
    // If it's just a path, prepend backend URL with /api
    return `${backendUrl}/api${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
  };

  const [previewUrl, setPreviewUrl] = useState(() => getFullImageUrl(currentImage));
  
  // Update previewUrl when currentImage changes
  useEffect(() => {
    const fullUrl = getFullImageUrl(currentImage);
    if (fullUrl !== previewUrl) {
      setPreviewUrl(fullUrl);
    }
  }, [currentImage, backendUrl]);

  const sizeClasses = {
    sm: 'w-16 h-16',
    md: 'w-24 h-24',
    lg: 'w-32 h-32'
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid File",
        description: "Please select an image file",
        variant: "destructive"
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Please select an image under 5MB",
        variant: "destructive"
      });
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);

    // Upload file
    await uploadImage(file);
    setShowOptions(false);
  };

  const uploadImage = async (file) => {
    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const endpoint = userType === 'driver' 
        ? `${backendUrl}/api/drivers/${userId}/upload/profile-photo`
        : `${backendUrl}/api/users/${userId}/upload/profile-photo`;

      const response = await axios.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const imageUrl = response.data.url || response.data.photo_url;
      const fullImageUrl = getFullImageUrl(imageUrl);
      setPreviewUrl(fullImageUrl);
      
      // Store in localStorage (store the relative URL for consistency)
      if (userType === 'driver') {
        localStorage.setItem('driver_photo', imageUrl);
      } else {
        localStorage.setItem('user_photo', imageUrl);
      }

      toast({
        title: "Photo Updated!",
        description: "Your profile picture has been saved",
      });

      if (onUploadSuccess) {
        onUploadSuccess(imageUrl);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload Failed",
        description: error.response?.data?.detail || "Failed to upload image",
        variant: "destructive"
      });
      // Revert preview
      setPreviewUrl(currentImage);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="relative inline-block">
      {/* Profile Picture Display */}
      <div 
        className={`${sizeClasses[size]} rounded-full overflow-hidden bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center cursor-pointer relative group shadow-lg border-4 border-white`}
        onClick={() => setShowOptions(!showOptions)}
      >
        {previewUrl ? (
          <img 
            src={previewUrl} 
            alt="Profile" 
            className="w-full h-full object-cover"
          />
        ) : (
          <User className="text-white" size={size === 'sm' ? 24 : size === 'md' ? 36 : 48} />
        )}
        
        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <Camera className="text-white" size={size === 'sm' ? 16 : 24} />
        </div>

        {/* Loading overlay */}
        {isUploading && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <Loader2 className="text-white animate-spin" size={24} />
          </div>
        )}
      </div>

      {/* Camera Badge */}
      <button 
        onClick={() => setShowOptions(!showOptions)}
        className="absolute bottom-0 right-0 w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white hover:bg-amber-600 transition-colors"
      >
        <Camera size={14} className="text-white" />
      </button>

      {/* Options Dropdown */}
      {showOptions && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white rounded-xl shadow-xl border-2 p-2 z-50 min-w-[160px]">
          <button
            onClick={() => cameraInputRef.current?.click()}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-amber-50 text-left"
          >
            <Camera size={18} className="text-amber-600" />
            <span className="text-sm font-medium">Take Photo</span>
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-amber-50 text-left"
          >
            <Upload size={18} className="text-amber-600" />
            <span className="text-sm font-medium">Upload Photo</span>
          </button>
          <button
            onClick={() => setShowOptions(false)}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 text-left text-gray-500"
          >
            <X size={18} />
            <span className="text-sm">Cancel</span>
          </button>
        </div>
      )}

      {/* Hidden File Inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="user"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};

export default ProfilePictureUpload;
